import React, { FunctionComponent } from 'react'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size } from '../../atoms/bodyText/BodyText'
import { CircleIcon } from '../../atoms/icons/CircleIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { tID } from '../../utils/utils'

export type ProviderChatThreadProps = {
  name: React.ReactNode
  date: React.ReactNode
  messagePreview: React.ReactNode
  isSelected: boolean
  hasUnreadMessage: boolean
  onPress: () => void
}

const Container = styled(PressableOpacity)(({ theme }) => ({
  position: 'relative',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  gap: theme.spacing['2px'],
  paddingLeft: theme.spacing['24px'],
  paddingRight: theme.spacing['16px'],
  paddingVertical: theme.spacing['24px'],
  backgroundColor: theme.colors.backgroundPrimary,
}))

const SelectedIndicator = styled.View(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: theme.spacing['24px'],
  bottom: theme.spacing['24px'],
  width: 3,
  borderTopRightRadius: 2,
  borderBottomRightRadius: 2,
  backgroundColor: theme.colors.borderActive,
}))

const NameAndDate = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const NameText = styled(BodyText)({
  flex: 1,
})

const NON_BREAKING_SPACE = '\u00A0'

export const ProviderChatThread: FunctionComponent<ProviderChatThreadProps> = ({
  name,
  date,
  messagePreview,
  isSelected,
  hasUnreadMessage,
  onPress,
}) => {
  const { colors } = useTheme()
  return (
    <Container
      testID={tID('ProviderChatThread')}
      onPress={onPress}
      accessibilityLabel={`${name}${hasUnreadMessage ? ', unread' : ''}, ${date}`}
    >
      {isSelected && <SelectedIndicator testID={tID('ProviderChatThread-selectedIndicator')} />}
      <NameAndDate>
        <NameText text={name} bold numberOfLines={1} color={isSelected ? colors.textActive : colors.textPrimary} />
        <BodyText text={date} size={Size.CAPTION} color={colors.textSecondary} numberOfLines={1} />
        {hasUnreadMessage && (
          <CircleIcon size={10} isFilled fillColor={colors.iconActive} testID={tID('ProviderChatThread-unreadIcon')} />
        )}
      </NameAndDate>
      {/* Non-breaking space keeps the preview line's height when there's no message (a plain space collapses on web), so every row stays the same height. */}
      <BodyText
        text={messagePreview || NON_BREAKING_SPACE}
        size={Size.SMALL}
        color={colors.textSecondary}
        numberOfLines={1}
      />
    </Container>
  )
}
